import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { AppAutoRefreshedMetadata } from '@repo/entities/UIEventMetadata';
import { Timer } from '@repo/libs/Timer';
import { systemTimer } from '@repo/libs/SystemTimer';
import { RecordAutoRefresh } from '../RecordAutoRefresh';
import { fetchBuildId } from './fetchBuildId';

const POLL_INTERVAL_MS = 30_000;
const RECORD_TIMEOUT_MS = 3_000;

/**
 * Hard-reloads the tab once a deploy finishes, so an all-day tab never runs
 * stale code. React Query's focus refetch checks on return to the tab.
 *
 * The build id must come from a plain `fetch`; see `buildIdRoute`.
 *
 * Each reload is recorded first, since the reload would kill the request.
 * Recording is best-effort: capped at `recordTimeoutMs` on an injected `Timer`,
 * failures swallowed. The poll uses React Query's own clock.
 */
export function useAutoRefreshOnNewVersion({
  getBuildId = fetchBuildId,
  reload = reloadWindow,
  recordReload = skipRecording,
  now = Date.now,
  pollIntervalMs = POLL_INTERVAL_MS,
  recordTimeoutMs = RECORD_TIMEOUT_MS,
  timer = systemTimer,
}: {
  getBuildId?: () => Promise<string>;
  reload?: () => void;
  recordReload?: RecordAutoRefresh;
  now?: () => number;
  pollIntervalMs?: number;
  recordTimeoutMs?: number;
  /** What the recording deadline waits on. */
  timer?: Timer;
} = {}) {
  // The first build id this tab saw. State, not a ref, because render reads
  // and writes it; setting state during render re-runs it with no extra paint.
  const [loadedBuildId, setLoadedBuildId] = useState<string | undefined>(
    undefined,
  );
  const loadedAtRef = useRef(now());
  const hasReloadedRef = useRef(false);

  const { data: buildId } = useQuery({
    queryKey: ['build-id'],
    queryFn: getBuildId,
    refetchInterval: pollIntervalMs,
  });

  if (buildId && !loadedBuildId) {
    setLoadedBuildId(buildId);
  }

  const newVersion = detectNewVersion({ loadedBuildId, buildId });

  useEffect(() => {
    // Reload once, so two deployments alternating behind the alias can't loop.
    if (!newVersion || hasReloadedRef.current) return;

    hasReloadedRef.current = true;
    void recordThenReload(newVersion);

    async function recordThenReload(version: NewVersion) {
      await recordWithinTimeout({
        recordReload,
        timer,
        timeoutMs: recordTimeoutMs,
        event: {
          path: window.location.pathname,
          minutesSincePageLoad: Math.round(
            (now() - loadedAtRef.current) / 60_000,
          ),
          ...version,
        },
      });
      reload();
    }
  }, [newVersion, reload, recordReload, now, recordTimeoutMs, timer]);
}

type NewVersion = Pick<AppAutoRefreshedMetadata, 'fromBuildId' | 'toBuildId'>;

// A deploy is a build id different from the one this tab loaded with.
function detectNewVersion({
  loadedBuildId,
  buildId,
}: {
  loadedBuildId: string | undefined;
  buildId: string | undefined;
}): NewVersion | undefined {
  return buildId && loadedBuildId && buildId !== loadedBuildId
    ? { fromBuildId: loadedBuildId, toBuildId: buildId }
    : undefined;
}

// A worker on a bad connection still needs the new code. The reload matters
// more than the row.
async function recordWithinTimeout({
  recordReload,
  timer,
  timeoutMs,
  event,
}: {
  recordReload: RecordAutoRefresh;
  timer: Timer;
  timeoutMs: number;
  event: AppAutoRefreshedMetadata;
}): Promise<void> {
  try {
    await Promise.race([
      recordReload(event),
      afterTimeout({ timer, ms: timeoutMs }),
    ]);
  } catch {
    // Swallowed on purpose; see above.
  }
}

function reloadWindow() {
  window.location.reload();
}

async function skipRecording() {}

function afterTimeout({ timer, ms }: { timer: Timer; ms: number }) {
  return new Promise<void>(function resolveAfter(resolve) {
    timer.setTimeout(resolve, ms);
  });
}
